import type { Artifact } from '../types.ts';
import { GENERAL_VOICES_TITLE, axisCoverage, axisDemandFor, formatDateRange, generalLabel, isGapAxis, stanceCounts } from '../lib/demand.ts';
import { DemandChip } from './DemandChip.tsx';
import { DemandQuoteList } from './DemandQuoteList.tsx';
import { DemandCoverage } from './DemandCoverage.tsx';

/** The quiet line for a run with no demand pass — a fact about the run, never about the market. */
export function DemandNotGathered(): JSX.Element {
  return <p className="demand-not-gathered">demand not gathered for this run</p>;
}

/**
 * One axis's demand: verdict key, stance counts, the general-voices count, date range, the
 * quotes, and the coverage line. Shared by the axis-level detail (below) and CellDetail, so the
 * same capability never tells two stories depending on which way the reader arrived.
 *
 * "N general" (design-system.md, "General voices on an axis") is the people the panel counted
 * toward the verdict without quoting them here: their quotes are about one product's feature
 * and sit under that product. Zero draws nothing.
 *
 * Three states are kept apart on purpose: no demand block ("not gathered"), a block with no entry
 * under this axis name ("not assessed"), and an entry whose verdict is `no_signal` ("we looked,
 * found nothing" — drawn with its coverage line, because only the denominator makes that honest).
 */
export function AxisDemandBody({
  artifact,
  axis,
  highlightQuery = '',
}: {
  artifact: Artifact;
  axis: string;
  highlightQuery?: string;
}): JSX.Element {
  const demand = artifact.demand;
  if (!demand) return <DemandNotGathered />;

  const entry = axisDemandFor(artifact, axis);
  if (!entry) {
    return (
      <p className="demand-not-gathered">
        demand not assessed for this capability — this run's demand panel has no entry under this name
      </p>
    );
  }

  const gap = isGapAxis(artifact.matrix, axis);
  const coverage = axisCoverage(demand, axis);
  const stances = stanceCounts(entry.stances);
  const general = generalLabel(entry);
  const range = formatDateRange(entry.date_range);
  const quotes = entry.quotes ?? [];

  return (
    <div className="axis-demand">
      <div className="axis-demand-head">
        <DemandChip verdict={entry.verdict} voices={entry.voices} gap={gap} />
        {stances.length > 0 && (
          <span className="axis-demand-meta">{stances.map((s) => `${s.stance} ${s.count}`).join(' · ')}</span>
        )}
        {general && (
          <span className="axis-demand-meta" title={GENERAL_VOICES_TITLE}>
            {general}
          </span>
        )}
        {range && <span className="axis-demand-meta">{range}</span>}
      </div>

      {entry.verdict === 'wanted' && gap && (
        <p className="axis-demand-note">Wanted, and absent from most of the vendors we could measure.</p>
      )}
      {/* Branch on the quotes, never on the verdict: `no_signal` means no demand voice and no
          rejection voice, and praise is neither — so a praise-only axis is `no_signal` WITH quotes,
          and hiding them would drop evidence the run paid for. */}
      {quotes.length > 0 ? (
        <>
          {entry.verdict === 'no_signal' && (
            <p className="axis-demand-note muted">
              No one asked for this or argued against it. The quotes below are praise: they corroborate supply, not demand.
            </p>
          )}
          <DemandQuoteList quotes={quotes} highlightQuery={highlightQuery} />
        </>
      ) : (
        <p className="axis-demand-note muted">
          We searched and kept no quotes for this capability. That describes the search below, not the market.
        </p>
      )}

      <DemandCoverage
        summary={coverage.summary}
        scope={coverage.scope === 'lane' ? 'this capability' : 'no lane for this capability; whole run'}
        searches={coverage.searches}
      />
    </div>
  );
}

/**
 * The axis-level demand detail: reached from the row-header key, so demand is readable without
 * first picking a vendor cell. Rendered by ArtifactView in the slot CellDetail uses — one detail
 * at a time under the matrix.
 */
export function AxisDemandDetail({
  artifact,
  axis,
  highlightQuery = '',
}: {
  artifact: Artifact;
  axis: string;
  highlightQuery?: string;
}): JSX.Element {
  return (
    <section className="artifact-section">
      <div className="tick">
        <span className="label">§ Demand · {axis}</span>
      </div>
      <div className="panel axis-demand-panel">
        <AxisDemandBody artifact={artifact} axis={axis} highlightQuery={highlightQuery} />
      </div>
    </section>
  );
}
